import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import type { ReportNewSection } from '@/pages/reports/new/hooks/useReportNew';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/auth';
import { useData } from '@/contexts/data';
import type { ReportComponent, ComponentDefinition } from '@/pages/reports/schemas';
import { COMPONENT_DEFINITIONS } from '@/pages/reports/schemas';
import { reportsService } from '@/services/reports';
import { isAppError } from '@/lib/errors';
import { hasValidHTMLContent } from '@/utils/html-content';
import { PARTNER_NONE } from '../constants';
import { DEFAULT_REPORT_LANG } from '@/pages/reports/new/constants';
import { normalizeReportLang, type ReportLocale } from '@/pages/reports/i18n';
import { normalizeHeroFirst } from '@/pages/reports/utils/heroOrder';
import { componentPropsToSave } from '@/pages/reports/utils/componentPropsToSave';
import { validateComponentProps as validateComponentPropsUtil } from '@/pages/reports/new/utils/validateComponentProps';
import { validateReportFinalize } from '@/pages/reports/utils/validateReportFinalize';
import { REPORT_STATUS_AWAITING_SEND } from '@/lib/reportStatus';
import { trackReportUpdated } from '@/lib/reportUpdated';
import {
  loadThematicSessionTemplates,
  createThematicSessionTemplate,
  updateThematicSessionTemplate,
  deleteThematicSessionTemplate,
  type ThematicSessionTemplate,
} from '@/pages/reports/utils/thematicSessionTemplates';

const AUTO_SAVE_DEBOUNCE_MS = 1000;
export function useReportEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const { partners } = useData();

  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [title, setTitle] = useState('');
  const [edition, setEdition] = useState('');
  const [components, setComponents] = useState<ReportComponent[]>([]);
  const [selectedComponent, setSelectedComponent] = useState<ComponentDefinition | null>(null);
  const [componentProps, setComponentProps] = useState<Record<string, any>>({});
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [titleError, setTitleError] = useState<string>('');
  const [editionError, setEditionError] = useState('');
  const [partnerError, setPartnerError] = useState('');
  const [langError, setLangError] = useState('');
  const [componentsError, setComponentsError] = useState('');
  const [editingComponentId, setEditingComponentId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [partnerId, setPartnerId] = useState<string>(PARTNER_NONE);
  const [lang, setLang] = useState<ReportLocale>(DEFAULT_REPORT_LANG);
  const [thematicTemplates, setThematicTemplates] = useState<ThematicSessionTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [openSection, setOpenSection] = useState<ReportNewSection>('basic');

  const hydratedRef = useRef(false);
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const persistInFlightRef = useRef(false);
  const persistReportRef = useRef<
    ((_options?: { silent?: boolean; status?: 'draft' | 'finished' | 'AWAITING_SEND' }) => Promise<boolean>) | null
  >(null);

  const partnerOptions = useMemo(
    () => [{ value: PARTNER_NONE, label: 'Nenhum' }, ...partners.map((p) => ({ value: p.id, label: p.name }))],
    [partners]
  );

  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const templates = await loadThematicSessionTemplates();
        setThematicTemplates(templates);
      } catch {
        toast({
          variant: 'error',
          title: 'Erro ao carregar modelos',
          description: 'Nao foi possivel carregar os modelos salvos.',
        });
      }
    };
    loadTemplates();
  }, [toast]);

  useEffect(() => {
    if (authLoading) return;
    const loadReport = async () => {
      if (!id) {
        navigate('/app/reports');
        return;
      }
      try {
        const report = await reportsService.getReportById(id, user?.email);
        if (!report) {
          navigate('/app/reports');
          return;
        }
        setTitle(report.title);
        setEdition(report.edition ?? '');
        setComponents(
          normalizeHeroFirst((report.content || []).filter((c) => c.type !== 'footer'))
        );
        setPartnerId(report.partner_id || PARTNER_NONE);
        setLang(normalizeReportLang(report.lang));
        setOpenSection('components');
      } catch (error) {
        if (isAppError(error) && error.status === 403) {
          setAccessDenied(true);
          setLoading(false);
          return;
        }
        console.error('Erro ao carregar report:', error);
        navigate('/app/reports');
      } finally {
        setLoading(false);
      }
    };
    loadReport();
  }, [id, navigate, user, authLoading]);

  const validateComponentProps = (): boolean => {
    if (!selectedComponent) return false;
    const errors = validateComponentPropsUtil(componentProps, selectedComponent);
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddComponent = (componentDef: ComponentDefinition) => {
    if (
      componentDef.type === 'hero' &&
      components.some((component) => component.type === 'hero')
    ) {
      return;
    }
    setSelectedComponent(componentDef);
    setComponentProps({});
    setEditingComponentId(null);
    setSelectedTemplateId('');
    if (componentDef.type === 'hero') {
      setComponentsError('');
    }
  };

  const handleEditComponent = (component: ReportComponent) => {
    const componentDef = COMPONENT_DEFINITIONS.find((c) => c.type === component.type);
    if (componentDef) {
      setSelectedComponent(componentDef);
      const props = component.props || {};
      if (component.type === 'news-card') {
        const itemsToContent = (items: string[]) =>
          (items || []).map((item) => `<p>${item}</p>`).join('');

        const normalizeLegacyTooltip = (t: any) =>
          t.items?.length && !t.content ? { ...t, content: itemsToContent(t.items) } : t;

        const buildLegacyTooltipList = (): any[] => {
          const hasLegacySingleTooltip =
            (props.tooltipItems && props.tooltipItems.length > 0) ||
            (props.tooltipTitleWord != null && String(props.tooltipTitleWord).trim() !== '');
          if (props.tooltips?.length) {
            return props.tooltips.map(normalizeLegacyTooltip);
          }
          if (hasLegacySingleTooltip) {
            return [{
              titleMode: 'ooooi',
              ooooiPrefix: 'ooooi',
              titleWord: props.tooltipTitleWord,
              titleWordBackgroundColor: props.tooltipTitleWordBackgroundColor,
              titleWordTextColor: props.tooltipTitleWordTextColor,
              content: itemsToContent(props.tooltipItems ?? []),
              backgroundColor: props.tooltipBackgroundColor ?? '#F2FF00',
              textColor: props.tooltipTextColor ?? '#000000',
            }];
          }
          return [];
        };

        if (!props.subsections?.length && (props.subtitle || props.tooltips?.length || props.tooltipTitleWord)) {
          const { tooltipTitleWord, tooltipTitleWordBackgroundColor, tooltipTitleWordTextColor, tooltipItems, tooltipBackgroundColor, tooltipTextColor, subtitle, tooltips, ...rest } = props;
          setComponentProps({
            ...rest,
            subsections: [{
              countries: [],
              content: subtitle ?? '',
              tooltips: buildLegacyTooltipList(),
            }],
          });
        } else {
          const { tooltipTitleWord, tooltipTitleWordBackgroundColor, tooltipTitleWordTextColor, tooltipItems, tooltipBackgroundColor, tooltipTextColor, ...rest } = props;
          setComponentProps({ ...rest });
        }
      } else {
        setComponentProps(props);
      }
      setEditingComponentId(component.id);
      setSelectedTemplateId('');
    }
  };

  const saveCurrentAsTemplate = async (name: string) => {
    if (selectedComponent?.type !== 'thematic-session') return;
    if (!name.trim()) return;
    try {
      const createdTemplate = await createThematicSessionTemplate({
        name: name.trim(),
        metadata: { ...componentProps },
      });
      setThematicTemplates((prev) => [
        createdTemplate,
        ...prev.filter((template) => template.id !== createdTemplate.id),
      ]);
      setSelectedTemplateId(createdTemplate.id);
      toast({
        title: 'Modelo salvo',
        description: 'Modelo criado com sucesso.',
      });
    } catch {
      toast({
        variant: 'error',
        title: 'Erro ao salvar modelo',
        description: 'Nao foi possivel salvar o modelo.',
      });
    }
  };

  const updateSelectedTemplate = async (name: string) => {
    if (selectedComponent?.type !== 'thematic-session' || !selectedTemplateId) return;
    if (!name.trim()) return;
    try {
      const updatedTemplate = await updateThematicSessionTemplate(selectedTemplateId, {
        name: name.trim(),
        metadata: { ...componentProps },
      });
      setThematicTemplates((prev) =>
        prev.map((template) => (template.id === selectedTemplateId ? updatedTemplate : template))
      );
      toast({
        title: 'Modelo atualizado',
        description: 'Modelo atualizado com sucesso.',
      });
    } catch {
      toast({
        variant: 'error',
        title: 'Erro ao atualizar modelo',
        description: 'Nao foi possivel atualizar o modelo.',
      });
    }
  };

  const deleteSelectedTemplate = async () => {
    if (selectedComponent?.type !== 'thematic-session' || !selectedTemplateId) return;
    try {
      await deleteThematicSessionTemplate(selectedTemplateId);
      setThematicTemplates((prev) => prev.filter((template) => template.id !== selectedTemplateId));
      setSelectedTemplateId('');
      toast({
        title: 'Modelo excluido',
        description: 'Modelo removido com sucesso.',
      });
    } catch {
      toast({
        variant: 'error',
        title: 'Erro ao excluir modelo',
        description: 'Nao foi possivel excluir o modelo.',
      });
    }
  };

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId);
    if (!templateId) return;
    const found = thematicTemplates.find((template) => template.id === templateId);
    if (!found) return;
    setComponentProps(found.props);
    setValidationErrors({});
  };

  const clearTemplateForm = () => {
    setSelectedTemplateId('');
    setComponentProps({});
    setValidationErrors({});
  };

  const handleSaveComponent = () => {
    if (!selectedComponent) return;
    if (!validateComponentProps()) return;

    const propsToSave = componentPropsToSave(selectedComponent, componentProps);

    if (editingComponentId) {
      setComponents((prev) => {
        const updated = prev.map((comp) =>
          comp.id === editingComponentId ? { ...comp, props: propsToSave } : comp
        );
        return normalizeHeroFirst(updated);
      });
      setEditingComponentId(null);
      setSelectedComponent(null);
    } else {
      if (
        selectedComponent.type === 'hero' &&
        components.some((component) => component.type === 'hero')
      ) {
        setSelectedComponent(null);
        setComponentProps({});
        setValidationErrors({});
        return;
      }
      const newComponent: ReportComponent = {
        id: crypto.randomUUID(),
        type: selectedComponent.type,
        props: propsToSave,
      };
      setComponents((prev) => normalizeHeroFirst([...prev, newComponent]));
      setSelectedComponent(null);
    }
    setComponentProps({});
    setValidationErrors({});
  };

  const handleRemoveComponent = (componentId: string) => {
    setComponents((prev) => normalizeHeroFirst(prev.filter((c) => c.id !== componentId)));
  };

  const handleMoveComponent = (componentId: string, direction: 'up' | 'down') => {
    setComponents((prev) => {
      const currentIndex = prev.findIndex((c) => c.id === componentId);
      if (currentIndex === -1) return prev;

      const current = prev[currentIndex];
      if (current.type === 'hero') return prev;

      const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (newIndex < 0 || newIndex >= prev.length) return prev;

      if (newIndex === 0) return prev;
      if (prev[newIndex].type === 'hero') return prev;

      const newComponents = [...prev];
      const [movedComponent] = newComponents.splice(currentIndex, 1);
      newComponents.splice(newIndex, 0, movedComponent);
      return normalizeHeroFirst(newComponents);
    });
  };

  const buildContentForSave = useCallback((): ReportComponent[] => {
    let list = components.filter((c) => c.type !== 'footer');
    if (editingComponentId && selectedComponent) {
      const propsToSave = componentPropsToSave(selectedComponent, componentProps);
      list = list.map((comp) =>
        comp.id === editingComponentId ? { ...comp, props: propsToSave } : comp
      );
    }
    return normalizeHeroFirst(list);
  }, [components, editingComponentId, selectedComponent, componentProps]);

  const persistReport = useCallback(
    async (options?: { silent?: boolean; status?: 'draft' | 'finished' | 'AWAITING_SEND' }) => {
      const silent = options?.silent === true;
      if (!title.trim()) {
        if (!silent) setTitleError('Título é obrigatório');
        return false;
      }
      if (!id) return false;
      if (persistInFlightRef.current) return false;

      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }

      persistInFlightRef.current = true;
      try {
        if (silent) {
          setAutoSaveStatus('saving');
        } else {
          setIsSaving(true);
        }
        await reportsService.updateReport(id, {
          title,
          ...(edition.trim() ? { edition: edition.trim() } : {}),
          content: buildContentForSave(),
          ...(partnerId && partnerId !== PARTNER_NONE ? { partner_id: partnerId } : {}),
          lang: normalizeReportLang(lang),
          ...(options?.status ? { status: options.status } : {}),
          trigger_source: silent ? 'autosave' : 'save_button',
        });
        setLastSavedAt(new Date());
        setAutoSaveStatus('saved');
        if (!silent) {
          trackReportUpdated(id, 'save_button');
          toast({
            variant: 'success',
            title: 'Conteúdo salvo com sucesso',
          });
        }
        return true;
      } catch (error) {
        console.error('Erro ao salvar report:', error);
        setAutoSaveStatus('error');
        if (!silent) {
          toast({
            variant: 'error',
            title: 'Erro ao salvar',
            description: 'Tente novamente ou entre em contato com o suporte.',
          });
        }
        return false;
      } finally {
        persistInFlightRef.current = false;
        if (!silent) setIsSaving(false);
      }
    },
    [buildContentForSave, edition, id, lang, partnerId, title, toast]
  );

  persistReportRef.current = persistReport;

  const handleSaveReport = useCallback(() => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    return persistReport();
  }, [persistReport]);

  const finalizeReport = useCallback(
    async (mode: 'close' | 'send') => {
      if (!id) return;

      const validation = validateReportFinalize({
        title,
        edition,
        partnerId,
        lang,
        components: buildContentForSave(),
      });

      setTitleError(validation.titleError);
      setEditionError(validation.editionError);
      setPartnerError(validation.partnerError);
      setLangError(validation.langError);
      setComponentsError(validation.componentsError);

      if (!validation.ok) {
        const element = document.getElementById(
          validation.target === 'components'
            ? 'report-components-section'
            : 'report-basic-section'
        );
        element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }

      const saved = await persistReport({ status: REPORT_STATUS_AWAITING_SEND });
      if (!saved) return;

      if (mode === 'send') {
        try {
          await reportsService.update(id, {
            status: REPORT_STATUS_AWAITING_SEND,
          });
        } catch {
          toast({
            variant: 'error',
            title: 'Não foi possível preparar o envio',
            description:
              'O report ainda está em rascunho. Tente finalizar novamente.',
          });
          return;
        }
        navigate(`/app/reports/${id}/view/send`);
        return;
      }

      navigate('/app/reports');
    },
    [id, title, edition, partnerId, lang, buildContentForSave, persistReport, navigate, toast]
  );

  const handleFinalizeClose = useCallback(() => {
    return finalizeReport('close');
  }, [finalizeReport]);

  const handleFinalizeSend = useCallback(() => {
    return finalizeReport('send');
  }, [finalizeReport]);

  useEffect(() => {
    if (loading || accessDenied) return;
    if (!hydratedRef.current) {
      hydratedRef.current = true;
      return;
    }
    if (!id || !title.trim()) return;

    setAutoSaveStatus((current) => (current === 'saving' ? current : 'idle'));

    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    autoSaveTimerRef.current = setTimeout(() => {
      persistReportRef.current?.({ silent: true });
    }, AUTO_SAVE_DEBOUNCE_MS);

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
    };
  }, [loading, accessDenied, id, title, edition, components, partnerId, lang, componentProps, editingComponentId, selectedComponent]);

  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, []);

  const handleDeleteReport = async () => {
    if (!id) return;
    try {
      setIsDeleting(true);
      await reportsService.deleteReport(id);
      toast({
        title: 'Report excluído',
        description: 'O report foi excluído com sucesso.',
      });
      navigate('/app/reports');
    } catch {
      toast({
        variant: 'error',
        title: 'Erro ao excluir',
        description: 'Não foi possível excluir o report.',
      });
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleTitleChange = (value: string) => {
    setTitle(value);
    if (titleError) setTitleError('');
  };

  const handleEditionChange = (value: string) => {
    setEdition(value);
    if (editionError) setEditionError('');
  };

  const handlePartnerChange = (value: string) => {
    setPartnerId(value);
    if (partnerError) setPartnerError('');
  };

  const handleLangChange = (value: string) => {
    setLang(normalizeReportLang(value));
    if (langError) setLangError('');
  };

  const handlePropChange = (key: string, value: string | any[]) => {
    setComponentProps((prev) => ({ ...prev, [key]: value }));
    if (validationErrors[key]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[key];
        return newErrors;
      });
    }
  };

  const cancelComponentEdit = () => {
    setSelectedComponent(null);
    setEditingComponentId(null);
    setSelectedTemplateId('');
  };

  const handleSectionOpenChange = useCallback(
    (section: ReportNewSection, open: boolean) => {
      if (!open) {
        setOpenSection(section === 'basic' ? 'components' : 'basic');
        return;
      }
      setOpenSection(section);
    },
    []
  );

  return {
    id,
    loading,
    accessDenied,
    title,
    titleError,
    edition,
    editionError,
    onEditionChange: handleEditionChange,
    components,
    selectedComponent,
    componentProps,
    validationErrors,
    editingComponentId,
    isSaving,
    isDeleting,
    deleteDialogOpen,
    setDeleteDialogOpen,
    partnerId,
    partnerError,
    lang,
    langError,
    componentsError,
    setPartnerId: handlePartnerChange,
    onLangChange: handleLangChange,
    partnerOptions,
    thematicTemplates,
    selectedTemplateId,
    onTemplateSelect: handleTemplateSelect,
    onClearAll: clearTemplateForm,
    handleTitleChange,
    handleAddComponent,
    handleEditComponent,
    handleSaveComponent,
    handleRemoveComponent,
    handleMoveComponent,
    handlePropChange,
    handleSaveReport,
    handleFinalizeClose,
    handleFinalizeSend,
    handleDeleteReport,
    cancelComponentEdit,
    saveCurrentAsTemplate,
    updateSelectedTemplate,
    deleteSelectedTemplate,
    navigate,
    autoSaveStatus,
    lastSavedAt,
    openSection,
    handleSectionOpenChange,
  };
}
